/**
 * Writes theme and locale back to the server (CLAUDE.md 14.2, 14.4).
 *
 * The second half of the two-layer persistence described in `ThemeProvider`. It lives
 * here, inside the authenticated tree, rather than in the provider itself, because the
 * provider is mounted above `AuthProvider` so that the login page has a theme -- and
 * therefore cannot call the API.
 *
 * No loop: `AuthProvider.applyUser` sets the local choice *from* the user record, so by
 * the time this runs the values already agree and nothing is sent.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from '@/api/client'
import { useAuth } from '@/auth/AuthProvider'
import { useTheme } from '@/theme/ThemeProvider'

export function PreferenceSync() {
  const { user } = useAuth()
  const { choice } = useTheme()
  const { i18n } = useTranslation()
  const inFlight = useRef(false)

  useEffect(() => {
    if (!user || inFlight.current) return

    const locale = i18n.language.slice(0, 2)
    const payload: Record<string, string> = {}
    if (choice !== user.theme_preference) payload.theme_preference = choice
    if (locale !== user.locale) payload.locale = locale
    if (Object.keys(payload).length === 0) return

    inFlight.current = true
    // Fire and forget: a preference that fails to save is a mild inconvenience on the
    // next device, not something worth interrupting the engineer over. It stays
    // correct locally either way.
    void api
      .patch('/auth/me', payload)
      .catch(() => undefined)
      .finally(() => {
        inFlight.current = false
      })
  }, [user, choice, i18n.language])

  return null
}
